import {
	ContractBondSubtype,
	ContractSuretyType,
	Dtos,
	formatToDollar,
	isCommercialSuretyBondType,
	isContractCategoryBondType,
} from "@inrev/common";
import { useFetchBondFormTemplates } from "@portal/api/bond-request";
import { buildCommercialBondTypeOptions } from "@portal/components/features/bond-request-submission/bondRequestBondTypeOptions";
import {
	createSummaryTo,
	SummaryCardSection,
	SummaryField,
	SummaryFieldGroup,
	SummarySection,
	type SummaryToFn,
} from "@portal/components/multiform";
import { RichTextDisplay } from "@portal/components/ui/RichTextDisplay";
import { useIsCommercialSuretyEnabled } from "@portal/hooks/useIsCommercialSuretyEnabled";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { useContext, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { match } from "ts-pattern";
import { z } from "zod";
import {
	bondFormTypeLabelMap,
	contractDamageOptions,
	contractHazardOptions,
} from "../../../../../../constants";
import { boolToYN, formatBondAmount, formatEmpty } from "../../../../../../utils";
import { NAICSCodesList } from "../../../../../admin/shared/NAICSCodeList";
import { BondRequestCreateContext } from "../../BondRequestCreateView";
import { type BondRequestFormValues, fp } from "../../schema";

type BidQuote = z.infer<typeof Dtos.SuretyQuote.Create.bidQuoteCreateSchema>;
type FinalQuote = z.infer<typeof Dtos.SuretyQuote.Create.finalQuoteCreateSchema>;
type B2FQuote = z.infer<typeof Dtos.SuretyQuote.Create.bidToFinalCreateSchema>;
type Project = BidQuote["project"];

const bondTypeLabels: Record<string, string> = {
	[ContractSuretyType.bid]: "Bid Bond",
	[ContractSuretyType.final]: "Final Bond",
	[ContractSuretyType.bid_to_final]: "Bid to Final",
};

const commercialBondTypeLabel = (bondType: string | undefined) =>
	buildCommercialBondTypeOptions(true).find((option) => option.value === bondType)?.label;

const BondTypeSummaryFields = ({
	contractSuretyType,
	to,
}: {
	contractSuretyType?: string;
	to: SummaryToFn;
}) => {
	const { getValues } = useFormContext<BondRequestFormValues>();
	const isCommercialSuretyEnabled = useIsCommercialSuretyEnabled();
	const { bondType, contractBondType } = getValues();

	if (isCommercialSuretyEnabled && bondType) {
		return (
			<>
				<SummaryField
					key="bondType"
					label="Bond Type:"
					value={formatEmpty(commercialBondTypeLabel(bondType))}
					fieldPath={fp("bondType")}
					to={to("bondType")}
				/>
				{isContractCategoryBondType(bondType) && (
					<SummaryField
						key="contractBondType"
						label="Contract Bond Type:"
						value={formatEmpty(
							contractBondType ? ContractBondSubtype.label(contractBondType) : undefined,
						)}
						fieldPath={fp("contractBondType")}
						to={to("contractBondType")}
					/>
				)}
			</>
		);
	}

	return (
		<SummaryField
			key="contractSuretyType"
			label="Bond Type:"
			value={formatEmpty(contractSuretyType ? bondTypeLabels[contractSuretyType] : undefined)}
			fieldPath={fp("quote.contractSuretyType")}
			to={to("contractSuretyType")}
		/>
	);
};

const scopeOfWorkField = (project: Project, to: SummaryToFn) => (
	<SummaryField
		key="scopeOfWork"
		label="What type of work will be performed?"
		vertical={true}
		value={
			<NAICSCodesList
				naicsCodes={project.scopeOfWork ?? []}
				emptyMessage="--"
				className="my-[8px] !bg-surface-light bg-opacity-100"
				hideExpand
				showCount
			/>
		}
		fieldPath={fp("quote.project.scopeOfWork")}
		to={to("scopeOfWork")}
	/>
);

const completionBondField = (quote: BidQuote | FinalQuote, to: SummaryToFn) => (
	<SummaryField
		key="completionBond"
		label="Is the principal a developer and/or is this a completion bond?"
		value={boolToYN(quote.completionBond)}
		fieldPath={fp("quote.completionBond")}
		to={to("completionBond")}
	/>
);

const tListingRequiredField = (quote: BidQuote | FinalQuote, to: SummaryToFn) => (
	<SummaryField
		key="tListingRequired"
		label="Does the bond require a Treasury Listing?"
		value={boolToYN(quote.tListingRequired)}
		fieldPath={fp("quote.tListingRequired")}
		to={to("tListingRequired")}
	/>
);

const projectFields = (project: Project, to: SummaryToFn) => [
	scopeOfWorkField(project, to),
	<SummaryField
		key="description"
		label="Project Description:"
		value={formatEmpty(project.description)}
		fieldPath={fp("quote.project.description")}
		to={to("description")}
	/>,
	<SummaryField
		key="city"
		label="Project City:"
		value={formatEmpty(project.address?.city)}
		fieldPath={fp("quote.project.address.city")}
		to={to("city")}
	/>,
	<SummaryField
		key="state"
		label="Project State:"
		value={formatEmpty(project.address?.state)}
		fieldPath={fp("quote.project.address.state")}
		to={to("state")}
	/>,
];

const sharedQuestionFields = (quote: BidQuote | FinalQuote, to: SummaryToFn) => [
	<SummaryField
		key="monthsToComplete"
		label="Months to complete project:"
		value={
			quote.project.monthsToComplete != null
				? `${quote.project.monthsToComplete}`
				: formatEmpty(undefined)
		}
		fieldPath={fp("quote.project.monthsToComplete")}
		to={to("monthsToComplete")}
	/>,
	<SummaryField
		key="contractWarrantyMonths"
		label="Contract warranty period (months):"
		value={
			quote.project.contractWarrantyMonths != null
				? `${quote.project.contractWarrantyMonths}`
				: formatEmpty(undefined)
		}
		fieldPath={fp("quote.project.contractWarrantyMonths")}
		to={to("contractWarrantyMonths")}
	/>,
	<SummaryField
		key="contractDamages"
		label="Does the contract include any of the following damages?"
		vertical={true}
		value={
			(quote.project.contractDamages?.length ?? 0) > 0
				? quote.project
						.contractDamages!.map(
							(d) => contractDamageOptions.find((o) => o.value === d)?.label ?? d,
						)
						.join(", ")
				: formatEmpty(undefined)
		}
		fieldPath={fp("quote.project.contractDamages")}
		to={to("contractDamages")}
	/>,
	<SummaryField
		key="contractHazards"
		label="Do any of the following apply to the contract?"
		vertical={true}
		value={
			(quote.project.contractHazards?.length ?? 0) > 0
				? quote.project
						.contractHazards!.map(
							(h) => contractHazardOptions.find((o) => o.value === h)?.label ?? h,
						)
						.join(", ")
				: formatEmpty(undefined)
		}
		fieldPath={fp("quote.project.contractHazards")}
		to={to("contractHazards")}
	/>,
	completionBondField(quote, to),
	tListingRequiredField(quote, to),
];

const attachmentsField = (quote: BidQuote | FinalQuote | B2FQuote, to: SummaryToFn) => (
	<SummaryField
		key="attachments"
		label="Attachments"
		vertical
		value={
			(quote.additionalInfo?.attachments?.length ?? 0) > 0
				? quote.additionalInfo!.attachments!.map((a) => `${a.name}.${a.extension}`)
				: formatEmpty(undefined)
		}
		fieldPath={fp("quote.additionalInfo.attachments")}
		to={to("attachments")}
	/>
);

const minimalExtraFields = (quote: BidQuote | FinalQuote, to: SummaryToFn) => [
	<SummaryField
		key="projectName"
		label="Project Name:"
		value={formatEmpty(quote.project.name)}
		fieldPath={fp("quote.project.name")}
		to={to("projectName")}
	/>,
	...(quote.contractSuretyType === ContractSuretyType.bid
		? [
				<SummaryField
					key="bidAmount"
					label="Bid Amount:"
					value={formatEmpty(formatToDollar(quote.bidAmount))}
					fieldPath={fp("quote.bidAmount")}
					to={to("bidAmount")}
				/>,
			]
		: []),
	<SummaryField
		key="comment"
		label="Submission Narrative"
		vertical
		value={
			quote.additionalInfo?.comment ? (
				<div className="line-clamp-3">
					<RichTextDisplay content={quote.additionalInfo.comment} />
				</div>
			) : (
				formatEmpty(quote.additionalInfo?.comment)
			)
		}
		fieldPath={fp("quote.additionalInfo.comment")}
		to={to("comment")}
	/>,
	attachmentsField(quote, to),
];

const BondFormSection = ({ pathname }: { pathname: string }) => {
	const { bondFormData } = useContext(BondRequestCreateContext);
	const { formState } = useFormContext();
	const { bondFormTemplates } = useFetchBondFormTemplates();

	const bondFormTemplate = useMemo(() => {
		if (bondFormData.templateId) {
			return bondFormTemplates.find((template) => template.id === bondFormData.templateId);
		}
	}, [bondFormData, bondFormTemplates]);

	const bondFormTypeLabel = useMemo(() => {
		const type = bondFormTemplate?.type ?? (bondFormData.upload.type || undefined);
		if (type) return bondFormTypeLabelMap[type];
	}, [bondFormData, bondFormTemplate]);

	const formValue =
		bondFormTemplate?.name ??
		(bondFormData.upload.files.length > 0
			? bondFormData.upload.files.map((f) => `${f.name}.${f.extension}`)
			: undefined);

	return (
		<SummarySection
			header="Bond Form"
			partial={
				formState.isSubmitted && !bondFormData.templateId && bondFormData.upload.files.length === 0
			}
			to={{ pathname, section: "bond", id: "quote.bondForm" }}
		>
			<SummaryFieldGroup>
				<SummaryField
					label="Form"
					value={formValue}
					vertical={Array.isArray(formValue) || undefined}
					to={{ pathname, section: "bond", id: "quote.bondForm" }}
				/>
				<SummaryField
					label="Type"
					value={bondFormTypeLabel}
					to={{ pathname, section: "bond", id: "quote.bondForm" }}
				/>
			</SummaryFieldGroup>
		</SummarySection>
	);
};

const BidBondSummary = ({
	quote,
	pathname,
	to,
	principalName,
	showPrincipal,
	isMinimalUIEnabled,
}: {
	quote: BidQuote;
	pathname: string;
	to: SummaryToFn;
	principalName?: string;
	showPrincipal?: boolean;
	isMinimalUIEnabled: boolean;
}) => (
	<SummaryCardSection header="Bond" to={{ pathname, section: "bond" }}>
		<SummaryFieldGroup>
			{[
				...(showPrincipal
					? [
							<SummaryField
								key="principal"
								label="Principal:"
								testId="summary-principal"
								value={formatEmpty(principalName)}
								fieldPath={fp("quote.principal.name")}
								to={to("principal")}
							/>,
						]
					: []),
				<BondTypeSummaryFields
					key="bondType"
					contractSuretyType={quote.contractSuretyType}
					to={to}
				/>,
				...(isMinimalUIEnabled
					? []
					: [
							<SummaryField
								key="bidDate"
								label="Bid Date:"
								value={formatEmpty(quote.bidDate)}
								fieldPath={fp("quote.bidDate")}
								to={to("bidDate")}
							/>,
							<SummaryField
								key="bidAmount"
								label="Bid Amount:"
								value={formatEmpty(formatToDollar(quote.bidAmount))}
								fieldPath={fp("quote.bidAmount")}
								to={to("bidAmount")}
							/>,
							<SummaryField
								key="bondAmount"
								label="Bond Amount:"
								value={formatBondAmount(quote.bondAmount)}
								fieldPath={fp("quote.bondAmount")}
								to={to("bondAmount")}
							/>,
							<SummaryField
								key="finalBondFormType"
								label="Final Bond Type:"
								value={
									quote.finalBondFormType
										? (bondFormTypeLabelMap[quote.finalBondFormType] ?? "")
										: formatEmpty(undefined)
								}
								fieldPath={fp("quote.finalBondFormType")}
								to={to("finalBondFormType")}
							/>,
							...projectFields(quote.project, to),
							...sharedQuestionFields(quote, to),
						]),
				...(isMinimalUIEnabled ? minimalExtraFields(quote, to) : []),
			]}
		</SummaryFieldGroup>

		{!isMinimalUIEnabled && (
			<SummarySection
				header="RFP / Bid Package"
				fieldPath={fp("quote.files")}
				to={{ pathname, section: "bond", id: "files" }}
			>
				<SummaryField
					label={(quote.files?.length ?? 0) > 1 ? "Files" : "File"}
					value={
						(quote.files?.length ?? 0) > 0
							? quote.files!.map((f) => `${f.name}.${f.extension}`)
							: formatEmpty(undefined)
					}
					vertical={(quote.files?.length ?? 0) > 1 ? true : undefined}
					to={to("files")}
				/>
			</SummarySection>
		)}

		<BondFormSection pathname={pathname} />
	</SummaryCardSection>
);

const FinalBondSummary = ({
	quote,
	pathname,
	to,
	principalName,
	showPrincipal,
	isMinimalUIEnabled,
}: {
	quote: FinalQuote;
	pathname: string;
	to: SummaryToFn;
	principalName?: string;
	showPrincipal?: boolean;
	isMinimalUIEnabled: boolean;
}) => (
	<SummaryCardSection header="Bond" to={{ pathname, section: "bond" }}>
		<SummaryFieldGroup>
			{[
				...(showPrincipal
					? [
							<SummaryField
								key="principal"
								label="Principal:"
								testId="summary-principal"
								value={formatEmpty(principalName)}
								fieldPath={fp("quote.principal.name")}
								to={to("principal")}
							/>,
						]
					: []),
				<BondTypeSummaryFields
					key="bondType"
					contractSuretyType={quote.contractSuretyType}
					to={to}
				/>,
				...(isMinimalUIEnabled
					? []
					: [
							<SummaryField
								key="bondAmount"
								label="Bond Amount:"
								value={formatBondAmount(quote.bondAmount)}
								fieldPath={fp("quote.bondAmount")}
								to={to("bondAmount")}
							/>,
							<SummaryField
								key="contractAmount"
								label="Contract Amount:"
								value={formatEmpty(formatToDollar(quote.contractAmount))}
								fieldPath={fp("quote.contractAmount")}
								to={to("contractAmount")}
							/>,
						]),
				...(isMinimalUIEnabled
					? minimalExtraFields(quote, to)
					: [...projectFields(quote.project, to), ...sharedQuestionFields(quote, to)]),
			]}
		</SummaryFieldGroup>

		{!isMinimalUIEnabled && (
			<SummarySection
				header="Contract"
				fieldPath={fp("quote.files")}
				to={{ pathname, section: "bond", id: "files" }}
			>
				<SummaryField
					label={(quote.files?.length ?? 0) > 1 ? "Files" : "File"}
					value={
						(quote.files?.length ?? 0) > 0
							? quote.files!.map((f) => `${f.name}.${f.extension}`)
							: formatEmpty(undefined)
					}
					vertical={(quote.files?.length ?? 0) > 1 ? true : undefined}
					to={to("files")}
				/>
			</SummarySection>
		)}

		<BondFormSection pathname={pathname} />
	</SummaryCardSection>
);

const BidToFinalSummary = ({
	quote,
	pathname,
	to,
	isMinimalUIEnabled,
}: {
	quote: B2FQuote;
	pathname: string;
	to: SummaryToFn;
	isMinimalUIEnabled: boolean;
}) => (
	<div className="flex flex-col space-y-[30px]">
		<SummaryCardSection header="Bid Results" to={{ pathname, section: "bond" }}>
			<SummaryFieldGroup>
				<SummaryField
					label="Low Bid Amount (Principal's Bid):"
					value={formatEmpty(formatToDollar(quote.actualBidAmount))}
					fieldPath={fp("quote.actualBidAmount")}
					to={to("actualBidAmount")}
				/>
				<SummaryField
					label="Second Lowest Bid:"
					value={formatEmpty(formatToDollar(quote.secondBid))}
					fieldPath={fp("quote.secondBid")}
					to={to("secondBid")}
				/>
				{!isMinimalUIEnabled && (
					<SummaryField
						label="Bid Date:"
						value={formatEmpty(quote.actualBidDate)}
						fieldPath={fp("quote.actualBidDate")}
						to={to("actualBidDate")}
					/>
				)}
			</SummaryFieldGroup>
		</SummaryCardSection>

		<SummaryCardSection header="Final Bond" to={{ pathname, section: "bond" }}>
			<SummaryFieldGroup>
				<SummaryField
					label="Final Bond Amount:"
					value={formatBondAmount(quote.bondAmount)}
					fieldPath={fp("quote.bondAmount")}
					to={to("bondAmount")}
				/>
				<SummaryField
					label="Contract Amount:"
					value={formatEmpty(formatToDollar(quote.contractAmount))}
					fieldPath={fp("quote.contractAmount")}
					to={to("contractAmount")}
				/>
				{!isMinimalUIEnabled && (
					<SummaryField
						label="Contract Date:"
						value={formatEmpty(quote.contractDate)}
						fieldPath={fp("quote.contractDate")}
						to={to("contractDate")}
					/>
				)}
				{!isMinimalUIEnabled && (
					<SummaryField
						label="Does the final bond require a Treasury Listing?"
						value={boolToYN(quote.tListingRequired)}
						fieldPath={fp("quote.tListingRequired")}
						to={to("tListingRequired")}
					/>
				)}
				{isMinimalUIEnabled && attachmentsField(quote, to)}
			</SummaryFieldGroup>

			{!isMinimalUIEnabled && (
				<SummarySection
					header="Contract"
					fieldPath={fp("quote.files")}
					to={{ pathname, section: "bond", id: "files" }}
				>
					<SummaryField
						label={(quote.files?.length ?? 0) > 1 ? "Files" : "File"}
						value={
							(quote.files?.length ?? 0) > 0
								? quote.files!.map((f) => `${f.name}.${f.extension}`)
								: formatEmpty(undefined)
						}
						vertical={true}
						to={to("files")}
					/>
				</SummarySection>
			)}

			<BondFormSection pathname={pathname} />
		</SummaryCardSection>
	</div>
);

export const BondSummary = ({
	includeCommercialFields = false,
	principalName: principalNameOverride,
}: {
	includeCommercialFields?: boolean;
	formData?: unknown;
	principalName?: string | null;
}) => {
	const { getValues } = useFormContext<BondRequestFormValues>();
	const { pathname } = useLocation();
	const { companies } = useContext(BondRequestCreateContext);
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const values = getValues();
	const quote = values.quote;
	const to = createSummaryTo(pathname, "bond");
	const principalName =
		principalNameOverride === undefined
			? quote?.principal?.name
			: (principalNameOverride ?? undefined);

	if (includeCommercialFields && isCommercialSuretyBondType(values.bondType)) {
		const commercialBond = values.commercialBond;
		const bondFormFiles = commercialBond?.bondForm?.upload?.files ?? [];

		return (
			<SummaryCardSection header="Bond" to={{ pathname, section: "bond" }}>
				<SummaryFieldGroup>
					<BondTypeSummaryFields to={to} />
					<SummaryField
						label="Bond Amount:"
						value={formatEmpty(formatToDollar(commercialBond?.bondAmount))}
						fieldPath={fp("commercialBond.bondAmount")}
						to={to("commercialBond.bondAmount")}
					/>
					<SummaryField
						label="Effective Date:"
						value={formatEmpty(commercialBond?.effectiveDate)}
						fieldPath={fp("commercialBond.effectiveDate")}
						to={to("commercialBond.effectiveDate")}
					/>
					<SummaryField
						label="Submission Narrative"
						value={formatEmpty(commercialBond?.submissionNarrative)}
						fieldPath={fp("commercialBond.submissionNarrative")}
						to={to("commercialBond.submissionNarrative")}
						vertical
					/>
					<SummaryField
						label={bondFormFiles.length === 1 ? "Bond Form" : "Bond Forms"}
						value={
							bondFormFiles.length > 0
								? bondFormFiles.map(
										(file: { name: string; extension: string }) => `${file.name}.${file.extension}`,
									)
								: formatEmpty(undefined)
						}
						fieldPath={fp("commercialBond.bondForm")}
						to={to("commercialBond.bondForm")}
						vertical={bondFormFiles.length > 1 || undefined}
					/>
				</SummaryFieldGroup>
			</SummaryCardSection>
		);
	}

	if (!quote) {
		return (
			<SummaryCardSection header="Bond" to={{ pathname, section: "bond" }}>
				<SummaryFieldGroup>
					<BondTypeSummaryFields to={to} />
				</SummaryFieldGroup>
			</SummaryCardSection>
		);
	}

	return match(quote)
		.with({ contractSuretyType: ContractSuretyType.bid }, (q) => (
			<BidBondSummary
				quote={q}
				pathname={pathname}
				to={to}
				principalName={principalName}
				showPrincipal={!!companies && companies.length > 0}
				isMinimalUIEnabled={isMinimalUIEnabled}
			/>
		))
		.with({ contractSuretyType: ContractSuretyType.final }, (q) => (
			<FinalBondSummary
				quote={q}
				pathname={pathname}
				to={to}
				principalName={principalName}
				showPrincipal={!!companies && companies.length > 0}
				isMinimalUIEnabled={isMinimalUIEnabled}
			/>
		))
		.with({ contractSuretyType: ContractSuretyType.bid_to_final }, (q) => (
			<BidToFinalSummary
				quote={q}
				pathname={pathname}
				to={to}
				isMinimalUIEnabled={isMinimalUIEnabled}
			/>
		))
		.otherwise(() => (
			<SummaryCardSection header="Bond" to={{ pathname, section: "bond" }}>
				<SummaryFieldGroup>
					<SummaryField
						key="principal"
						label="Principal:"
						value={formatEmpty(principalName)}
						fieldPath={fp("quote.principal.name")}
						to={to("principal")}
					/>
					<BondTypeSummaryFields to={to} />
				</SummaryFieldGroup>
			</SummaryCardSection>
		));
};
